import { css } from "@emotion/react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { motion } from "motion/react"

export interface BaseTakePhotoRef {
    startCamera: () => Promise<void>
    takePhoto: () => void
    restartCamera: () => void
    switchCamera: () => Promise<void>
}

export interface BaseTakePhotoProps {
    onCameraStart?: (success: boolean, error?: Error) => void
    onCameraReady?: () => void
    onCaptureImage?: (imageData: string) => void
    onCaptureImageFile?: (file: File) => void
    defaultFacingMode?: "user" | "environment"
}

const BaseTakePhotoV2 = forwardRef<BaseTakePhotoRef, BaseTakePhotoProps>(
    (
        {
            onCameraStart,
            onCameraReady,
            onCaptureImage,
            onCaptureImageFile,
            defaultFacingMode = "user",
        },
        ref,
    ) => {
        const videoRef = useRef<HTMLVideoElement | null>(null)
        const canvasRef = useRef<HTMLCanvasElement | null>(null)
        const [capturedImage, setCapturedImage] = useState<string | null>(null)
        const [facingMode, setFacingMode] = useState<"user" | "environment">(defaultFacingMode)
        const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
        const [isLoading, setIsLoading] = useState<boolean>(false)
        const isMounted = useRef(true)
        const hasCalledReadyRef = useRef(false)
        const [isReady, setIsReady] = useState(false)
        const isSwitchingRef = useRef(false)
        const currentFacingModeRef = useRef(defaultFacingMode)

        // 更新 ref 以追蹤當前的 facingMode
        useEffect(() => {
            currentFacingModeRef.current = facingMode
        }, [facingMode])

        // 在視頻準備好時的處理
        const handleVideoReady = useCallback(() => {
            setIsReady(true)
            if (isMounted.current && !hasCalledReadyRef.current && onCameraReady) {
                hasCalledReadyRef.current = true
                onCameraReady()
            }
        }, [onCameraReady])

        // 停止當前相機流
        const stopCurrentStream = useCallback(() => {
            if (cameraStream) {
                cameraStream.getTracks().forEach((track) => track.stop())
                setCameraStream(null)
            } else if (videoRef.current && videoRef.current.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream
                stream.getTracks().forEach((track) => track.stop())
                // 避免直接設null導致畫面閃爍
                // videoRef.current.srcObject = null
            }
            // 重置 ready 狀態
            hasCalledReadyRef.current = false
        }, [cameraStream])

        // 開啟相機
        const startCamera = useCallback(async (): Promise<void> => {
            if (isLoading || !isMounted.current) return

            // 重置 ready 狀態
            hasCalledReadyRef.current = false
            setIsLoading(true)
            setIsReady(false)

            try {
                // 先停止當前相機流，但保留舊畫面直到新流準備好
                stopCurrentStream()

                // 使用 ref 獲取最新的 facingMode 值
                const currentMode = currentFacingModeRef.current

                const constraints = {
                    video: {
                        facingMode:
                            currentMode === "user" ? { ideal: "user" } : { ideal: "environment" },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                }

                const stream = await navigator.mediaDevices.getUserMedia(constraints)

                if (isMounted.current && videoRef.current) {
                    // 新流準備好後再替換
                    videoRef.current.srcObject = stream
                    setCameraStream(stream)
                    onCameraStart?.(true)

                    // video的canplay事件會觸發handleVideoReady
                } else {
                    // 如果組件已卸載，釋放資源
                    stream.getTracks().forEach((track) => track.stop())
                }
            } catch (err) {
                console.error("無法存取相機: ", err)

                // 如果指定鏡頭模式失敗，嘗試使用更寬鬆的設置
                if (isMounted.current) {
                    try {
                        const fallbackStream = await navigator.mediaDevices.getUserMedia({
                            video: {
                                width: { ideal: 640 },
                                height: { ideal: 480 },
                            },
                            audio: false,
                        })

                        if (isMounted.current && videoRef.current) {
                            videoRef.current.srcObject = fallbackStream
                            setCameraStream(fallbackStream)
                            onCameraStart?.(true)

                            // video的canplay事件會觸發handleVideoReady
                        } else {
                            fallbackStream.getTracks().forEach((track) => track.stop())
                        }
                    } catch (fallbackErr) {
                        if (isMounted.current) {
                            onCameraStart?.(
                                false,
                                fallbackErr instanceof Error
                                    ? fallbackErr
                                    : new Error(String(fallbackErr)),
                            )
                        }
                    }
                }
            } finally {
                if (isMounted.current) {
                    setIsLoading(false)
                    // 如果是相機切換，重置切換標記
                    if (isSwitchingRef.current) {
                        isSwitchingRef.current = false
                    }
                }
            }
        }, [onCameraStart, stopCurrentStream, isLoading])

        // 改進切換相機的方法
        const switchCamera = useCallback(async (): Promise<void> => {
            // 防止重複切換或在加載過程中切換
            if (isLoading || isSwitchingRef.current || !isMounted.current) return

            // 設置切換標記，防止多次點擊
            isSwitchingRef.current = true

            // 切換 facingMode
            const newMode = currentFacingModeRef.current === "user" ? "environment" : "user"
            currentFacingModeRef.current = newMode
            setFacingMode(newMode)

            // 立即啟動相機，不使用 setTimeout
            await startCamera()
        }, [isLoading, startCamera])

        // 初始化相機，僅在組件掛載時執行一次
        useEffect(() => {
            isMounted.current = true
            if (!capturedImage) {
                startCamera()
            }

            return () => {
                isMounted.current = false
                stopCurrentStream()
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []) // 移除依賴以避免重新啟動

        // 拍照
        const takePhoto = useCallback((): void => {
            if (isLoading) return

            const video = videoRef.current
            const canvas = canvasRef.current

            if (video && canvas) {
                const ctx = canvas.getContext("2d")

                if (ctx) {
                    // 設置 canvas 大小與影片一致
                    canvas.width = video.videoWidth
                    canvas.height = video.videoHeight

                    // 處理鏡像問題
                    if (currentFacingModeRef.current === "user") {
                        ctx.translate(canvas.width, 0)
                        ctx.scale(-1, 1)
                    }

                    // 繪製影像到 canvas
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

                    // 重置變換
                    if (currentFacingModeRef.current === "user") {
                        ctx.setTransform(1, 0, 0, 1, 0, 0)
                    }

                    // 轉換為 base64 圖片，使用 jpeg 格式和較低品質以減小大小
                    const imageData = canvas.toDataURL("image/jpeg", 0.7)

                    setCapturedImage(imageData)
                    onCaptureImage?.(imageData)
                    if (onCaptureImageFile) {
                        canvas.toBlob(
                            (blob) => {
                                if (blob) {
                                    onCaptureImageFile(
                                        new File([blob], "image.png", { type: "image/png" }),
                                    )
                                }
                            },
                            "image/png",
                            0.7,
                        )
                    }
                    // 停止相機
                    stopCurrentStream()
                }
            }
        }, [isLoading, onCaptureImage, onCaptureImageFile, stopCurrentStream])

        const restartCamera = useCallback(() => {
            if (isLoading) return

            // 先清除圖片，讓影片顯示出來
            setCapturedImage(null)
            // 直接啟動相機，不使用 setTimeout
            startCamera()
        }, [isLoading, startCamera])

        useImperativeHandle(ref, () => ({
            startCamera,
            takePhoto,
            restartCamera,
            switchCamera,
        }))

        return (
            <motion.div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    justify-content: center;
                    background-color: #000;
                    position: relative;
                    overflow: hidden;
                `}
                initial={{ opacity: 0 }}
                animate={{ opacity: isReady ? 1 : 0 }}
                exit={{ opacity: 0 }}
            >
                {!capturedImage ? (
                    <>
                        <video
                            css={css`
                                height: auto;
                                width: 100%;
                                flex: 1;
                                object-fit: cover;
                                transform: ${facingMode === "user" ? "scaleX(-1)" : "none"};
                                transition: opacity 0.2s ease-in-out;
                                opacity: ${isLoading ? 0.7 : 1};
                            `}
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            onCanPlay={handleVideoReady}
                        />
                        {isLoading && (
                            <div
                                css={css`
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    right: 0;
                                    bottom: 0;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    background-color: rgba(0, 0, 0, 0.3);
                                    z-index: 2;
                                `}
                            >
                                <div
                                    css={css`
                                        width: 48px;
                                        height: 48px;
                                        border: 4px solid rgba(255, 255, 255, 0.3);
                                        border-top: 4px solid white;
                                        border-radius: 50%;
                                        animation: spin 1s linear infinite;
                                        @keyframes spin {
                                            0% {
                                                transform: rotate(0deg);
                                            }
                                            100% {
                                                transform: rotate(360deg);
                                            }
                                        }
                                    `}
                                ></div>
                            </div>
                        )}
                    </>
                ) : (
                    <img
                        css={css`
                            height: 100%;
                            flex: 1;
                            object-fit: cover;
                            transform: ${facingMode === "user" ? "scaleX(-1)" : "none"};
                        `}
                        src={capturedImage}
                        alt="拍攝照片"
                    />
                )}
                <canvas
                    ref={canvasRef}
                    style={{ display: "none" }}
                />
            </motion.div>
        )
    },
)

BaseTakePhotoV2.displayName = "BaseTakePhotoV2"

export default BaseTakePhotoV2
